import { z } from "zod";

// Auth schemas
export const authTokensSchema = z.object({
  access_token: z.string(),
  expires_in: z.number(),
});

export const authEmployeeSchema = z.object({
  id: z.number(),
  name: z.string().optional(),
  whatsapp_number: z.string(),
  unique_identifier: z.string().nullable().optional(),
  has_pin: z.boolean(),
});

export const authInitializeRequestSchema = z.object({
  whatsapp_number: z.string().min(1, "Phone Number is required"),
  pin_code: z.string().length(4, "PIN must be 4 digits").regex(/^\d{4}$/, "PIN must contain only digits"),
  unique_identifier: z.string().optional(),
  device_id: z.string().optional(),
  platform: z.string().optional(),
  fcm_token: z.string().optional(),
});

export const authLoginRequestSchema = z.object({
  whatsapp_number: z.string().min(1, "Phone Number is required"),
  pin_code: z.string().length(4, "PIN must be 4 digits").regex(/^\d{4}$/, "PIN must contain only digits"),
  device_id: z.string().optional(),
  platform: z.string().optional(),
  fcm_token: z.string().optional(),
});

export const authResponseSchema = z.object({
  access_token: z.string(),
  expires_in: z.number(),
  employee: authEmployeeSchema,
  device_trusted: z.boolean(),
  message: z.string().optional(),
});

// Task schemas
export const taskStatusSchema = z.enum(["pending", "awaiting_photo", "awaiting_confirmation", "blocked", "escalated", "completed", "cancelled"]);
export const taskPrioritySchema = z.enum(["low", "medium", "high", "urgent"]);

export const taskSchema = z.object({
  id: z.number(),
  title: z.string(),
  description: z.string().nullable().optional(),
  status: taskStatusSchema,
  priority: taskPrioritySchema.nullable().optional(),
  assignee: z.object({
    id: z.number(),
    name: z.string(),
    avatarUrl: z.string().optional(),
  }).nullable().optional(),
  dueAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  messageCount: z.number().optional(),
  lastViewedAt: z.string().nullable().optional(),
}).passthrough(); // Allow additional fields that might be present

export const messageSchema = z.object({
  id: z.number(),
  taskId: z.number().nullable(), // Allow null for general messages not tied to specific tasks
  createdAt: z.string(),
  direction: z.enum(["incoming", "outgoing"]),
  senderName: z.string().nullable().optional(),
  body: z.string(),
  photoUrl: z.string().url().nullable().optional(), // URL of the photo if message contains a photo
}).passthrough(); // Allow additional fields that might be present

export const paginatedSchema = <T>(itemSchema: z.ZodSchema<T>) =>
  z.object({
    data: z.array(itemSchema),
    nextCursor: z.string().nullable().optional(),
  }).passthrough(); // Allow additional fields that might be present

export const sendMessageRequestSchema = z.object({
  body: z.string(), // Allow empty string if photoUrl is present
  photos: z.array(z.any()).optional(), // Photos will be validated separately
  photoUrl: z.string().url().optional(), // Public URL of uploaded photo
}).refine(
  (data) => data.body.trim().length > 0 || data.photoUrl,
  {
    message: "Message must have either text or photoUrl",
  }
);

// Export types
export type AuthTokens = z.infer<typeof authTokensSchema>;
export type AuthEmployee = z.infer<typeof authEmployeeSchema>;
export type AuthInitializeRequest = z.infer<typeof authInitializeRequestSchema>;
export type AuthLoginRequest = z.infer<typeof authLoginRequestSchema>;
export type AuthResponse = z.infer<typeof authResponseSchema>;
export type TaskStatus = z.infer<typeof taskStatusSchema>;
export type TaskPriority = z.infer<typeof taskPrioritySchema>;
export type Task = z.infer<typeof taskSchema>;
export type Message = z.infer<typeof messageSchema>;
export type Paginated<T> = z.infer<ReturnType<typeof paginatedSchema>>;
export type SendMessageRequest = z.infer<typeof sendMessageRequestSchema>;
